interface NewestSortOptions {
  dateKeys: string[];
  numericFallbackKeys?: string[];
}

const readNumericValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const readDateValue = (value: unknown): number | null => {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const getFirstDateValue = <T extends object>(item: T, keys: string[]): number | null => {
  const record = item as Record<string, unknown>;

  for (const key of keys) {
    const value = readDateValue(record[key]);
    if (value !== null) {
      return value;
    }
  }

  return null;
};

const getFirstNumericValue = <T extends object>(item: T, keys: string[]): number | null => {
  const record = item as Record<string, unknown>;

  for (const key of keys) {
    const value = readNumericValue(record[key]);
    if (value !== null) {
      return value;
    }
  }

  return null;
};

export const compareByNewest = <T extends object>(
  left: T,
  right: T,
  options: NewestSortOptions,
): number => {
  const leftDate = getFirstDateValue(left, options.dateKeys);
  const rightDate = getFirstDateValue(right, options.dateKeys);

  if (leftDate !== null && rightDate !== null && leftDate !== rightDate) {
    return rightDate - leftDate;
  }

  if (leftDate !== null && rightDate === null) {
    return -1;
  }

  if (leftDate === null && rightDate !== null) {
    return 1;
  }

  const fallbackKeys = options.numericFallbackKeys ?? ['id'];
  const leftFallback = getFirstNumericValue(left, fallbackKeys);
  const rightFallback = getFirstNumericValue(right, fallbackKeys);

  if (leftFallback !== null && rightFallback !== null && leftFallback !== rightFallback) {
    return rightFallback - leftFallback;
  }

  if (leftFallback !== null && rightFallback === null) {
    return -1;
  }

  if (leftFallback === null && rightFallback !== null) {
    return 1;
  }

  return 0;
};

export const sortByNewest = <T extends object>(
  items: readonly T[],
  options: NewestSortOptions,
): T[] => [...items].sort((left, right) => compareByNewest(left, right, options));
